import {_} from 'lodash';
import {isNaN} from "lodash";
import * as ko from "knockout";
import {AvailableLanguages, Delimiter, ILanguage} from "../constants/Language";
import {
    ISyncField,
    ITranslationParams,
    TBaseOrObservableOrFunctionType,
    TBaseOrObservableType,
    TRouteIdOrDefinition,
    TSyncfield
} from "../index";
import {storeObservable} from "./sessionstore";
import {_d, getAnyValue, getObjectDefinition} from "./misc";
import {isNullOrUndefined} from "./value";
import {
    MAX_CONCURRENT_REQUESTS,
    ObjectTypeId,
    ObjectTypeId2ImportFormatId,
    ObjectTypeid2Key,
    Permission,
    REQUEST_THROTTLE_QUICK
} from "../constants";
import pubsub from "../pubsub";
import {config, global} from "../appContext";
import {TServiceName} from "../constants/ApiServices";
import {ImportFormatId} from "../constants/ImportFormats";
import {getRouteLabel} from "../constants/RouteDefinitions";
import {moment} from 'moment';
import PromiseRequest from "../req/PromiseRequest";
import {nsSyncFields} from "../req/nsSyncFields";
import {nsLanguage} from "./nsLanguage";
import {KOSubscriptionTracker} from "./KOSubscriptionTracker";
import {parseTranslationAlias} from "./translationAlias";

const logConfig: Set<string> = new Set()
const logTranslation: Set<string> = new Set()

interface IFetchTranslationParams {
    objecttypeid: ObjectTypeId
    languageid: number
    importformatid: ImportFormatId
    orgnr?: string
    token?: string
}

const AvailableObjectTypeIds = [
    ObjectTypeId.User,
    ObjectTypeId.Department,
    ObjectTypeId.LegalUnit,
    ObjectTypeId.Group,
    ObjectTypeId.Function,
    ObjectTypeId.History,
    ObjectTypeId.Event,
    ObjectTypeId.FormData,
    ObjectTypeId.FunctionAlias,
    ObjectTypeId.ExternalContact,
    ObjectTypeId.FormFunction,
    ObjectTypeId.ApiUser,
    ObjectTypeId.FEStandard,
] as const

const ObjectType2MinimumPermission: Record<typeof AvailableObjectTypeIds[number], Permission> = {
    [ObjectTypeId.User]           : Permission.LoggedIn,
    [ObjectTypeId.Department]     : Permission.LoggedIn,
    [ObjectTypeId.LegalUnit]      : Permission.CompanyAdmin,
    [ObjectTypeId.Group]          : Permission.LoggedIn,
    [ObjectTypeId.Function]       : Permission.DepartmentAdmin,
    [ObjectTypeId.History]        : Permission.DepartmentAdmin,
    [ObjectTypeId.Event]          : Permission.DepartmentAdmin,
    [ObjectTypeId.FormData]       : Permission.LoggedIn,
    [ObjectTypeId.FunctionAlias]  : Permission.DepartmentAdmin,
    [ObjectTypeId.ExternalContact]: Permission.DepartmentAdmin,
    [ObjectTypeId.FormFunction]   : Permission.DepartmentAdmin,
    [ObjectTypeId.ApiUser]        : Permission.CompanyAdmin,
    [ObjectTypeId.FEStandard]     : Permission.None,
} as const

export class LanguageManager {

    User!: LanguageInstance
    Department!: LanguageInstance
    LegalUnit!: LanguageInstance
    Group!: LanguageInstance
    Function!: LanguageInstance
    History!: LanguageInstance
    Event!: LanguageInstance
    // System: LanguageInstance
    FormData!: LanguageInstance
    // Message: LanguageInstance
    // Ruleset: LanguageInstance
    FunctionAlias!: LanguageInstance
    ExternalContact!: LanguageInstance
    FormFunction!: LanguageInstance
    ApiUser!: LanguageInstance
    // QueryAlias: LanguageInstance
    FEStandard!: LanguageInstance
    // BEStandard: LanguageInstance

    private readonly languageInstances: LanguageInstance[] = []
    public readonly selectedLanguage: KnockoutObservable<ILanguage> = storeObservable('language')
    public readonly AvailableLanguages = AvailableLanguages
    public readonly isReady: KnockoutObservable<boolean> = ko.observable(false)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()

    constructor() {
        const validatorFactory = (objectTypeId: typeof AvailableObjectTypeIds[number]) => {
            if (objectTypeId == ObjectTypeId.FEStandard) {
                return () => true
            } else {
                return () => {
                    const isLoggedIn = global.login.isLoggedIn()
                    const permission_user = global.permissionValidator.role()
                    const permission_threshold = ObjectType2MinimumPermission[objectTypeId]
                    if (permission_threshold == Permission.LoggedIn) {
                        return global.login.isLoggedIn()
                    }
                    return isLoggedIn && permission_user >= permission_threshold
                }
            }
        }
        AvailableObjectTypeIds.forEach(objectTypeId => {
            const validator = validatorFactory(objectTypeId);
            const instance = new LanguageInstance({
                importFormatId: ObjectTypeId2ImportFormatId[objectTypeId],
                objectTypeId: objectTypeId,
                selectedLanguage: this.selectedLanguage,
                validator: validator,
            })
            this.languageInstances[objectTypeId] = instance
            this[ObjectTypeid2Key[objectTypeId]] = instance // allows ie. lm_.Department.t('SourceId')
        })

        if (isNullOrUndefined(this.selectedLanguage())) {
            this.selectedLanguage(this.getPreferredLanguage())
        } else {
            const languageItem = this.AvailableLanguages.find(al => al.id == this.selectedLanguage().id)
            if (languageItem) {
                this.selectedLanguage(languageItem)
            }
        }
    }

    // called from global (G) (when global is readily available)
    public init() {
        _d.groupCollapsed('Missing config translations')
        _d.log(logConfig)
        _d.groupEnd()
        _d.groupCollapsed('Missing implemented translations')
        _d.log(logTranslation)
        _d.groupEnd()

        this.kosub.subscribe(this.selectedLanguage, () => this._init())

        let initTimeoutId: number
        pubsub.on(global, 'cache.removed', (e: JQuery.Event, serviceName: TServiceName) => {
            clearTimeout(initTimeoutId)
            if (serviceName == 'getsyncfields') {
                initTimeoutId = setTimeout(() => this._init(), REQUEST_THROTTLE_QUICK)
            }
        })

        this.kosub.subscribe(global.login.selectedOrganization, () => this._init())

        return this._init()
    }

    private _init() {
        this.isReady(false)
        const languageKey = this.selectedLanguage().languageKey ?? this.selectedLanguage().key;
        moment.locale(languageKey)
        const instances = this.languageInstances.slice()

        const doNext = (): Promise<any> => {
            if (!instances.length) {
                return Promise.resolve()
            }
            const instance = instances.pop()
            if (!instance) {
                return Promise.resolve()
            }
            return instance.init()
                .then(() => doNext())
        }

        const promises = []
        for (let i = 0; i < Math.min(instances.length, MAX_CONCURRENT_REQUESTS); i++) {
            promises.push(doNext())
        }
        return Promise.all(promises)
            .then(() => this.isReady(true))
    }

    private getPreferredLanguage(): ILanguage {
        return this.AvailableLanguages.find(al => navigator.languages.find(nl => nl.startsWith(al.key)))
            ?? this.AvailableLanguages.find(al => al.key == config.DEFAULT_LANGUAGE)
            ?? this.AvailableLanguages[0]!
    }

    getInstance(objectTypeId: ObjectTypeId = ObjectTypeId.FEStandard): LanguageInstance {
        return this.languageInstances[objectTypeId]!
    }

    dispose() {
        this.kosub.unsuscribeAll()
        pubsub.off(this)
    }

}

interface ILanguageInstanceParams {
    objectTypeId: ObjectTypeId
    selectedLanguage: KnockoutObservable<ILanguage>
    importFormatId?: ImportFormatId | undefined
    validator: () => boolean
}

export class LanguageInstance {
    public readonly isReady: KnockoutObservable<boolean> = ko.observable(false)
    public readonly translations: KnockoutObservableArray<ISyncField> = ko.observableArray(null)
    public readonly promiseRequest: PromiseRequest = new PromiseRequest('getsyncfields')
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    /** Tracks `@alias` hops within a single `t()` call to break A→B→A cycles. */
    private readonly aliasResolveStack: string[] = []

    public readonly objectTypeId: ObjectTypeId
    public readonly selectedLanguage: KnockoutObservable<ILanguage>
    public readonly importFormatId: ImportFormatId | undefined
    private readonly validator: (() => boolean) | undefined

    constructor(params: ILanguageInstanceParams) {
        this.objectTypeId = params.objectTypeId
        this.selectedLanguage = params.selectedLanguage
        this.importFormatId = params.importFormatId
        this.validator = params.validator
    }

    init(): Promise<any> {
        this.isReady(false)
        if (!this.validator?.()) {
            return Promise.resolve()
        }
        const params: any = {
            objecttypeid: this.objectTypeId,
            languageid: this.selectedLanguage().id,
            importformatid: this.getImportFormatId(),
        };
        if (!global.login?.isLoggedIn()) {
            params.orgnr = ''
            params.token = ''
        }
        let translations: ISyncField[]
        let translations_default: ISyncField[]
        return Promise.resolve()
            .then(() => {
                return this
                    .fetchTranslations(params)
                    .then((data: ISyncField[]) => translations = data)
            })
            .then(() => {
                const defaultLanguage = AvailableLanguages.find(o => o.key == config.DEFAULT_LANGUAGE)
                if (!defaultLanguage || this.selectedLanguage().id == defaultLanguage.id) {
                    // no need to load default language
                    this.translations(translations)
                    return
                }
                const params_default: IFetchTranslationParams = _.defaultsDeep({languageid: defaultLanguage.id}, params)
                return this
                    .fetchTranslations(params_default)
                    .then((data: ISyncField[]) => translations_default = data)
                    .then(() => {
                        const translations_combined = translations.map(translation => {
                            if (!translation.displayname) {
                                // use default language translation
                                const translation_default = translations_default.find(translation_default => translation?.syncfieldid == translation_default?.syncfieldid)
                                return translation_default ?? translation
                            }
                            return translation
                        })
                        this.translations(translations_combined)
                    })
            })
            .then(() => {
                this.isReady(true)
            })
    }

    private getImportFormatId() {
        return this.importFormatId ?? global.login.selectedOrganization()?.importformat.id ?? ImportFormatId.Visma
    }

    private fetchTranslations(params: IFetchTranslationParams): Promise<ISyncField[]> {
        this.promiseRequest?.abort()
        return this.promiseRequest.request(params)
    }

    // factory for creating a translation instance
    public $t(obj: TBaseOrObservableOrFunctionType<string | ITranslationParams>, defaults?: Partial<ITranslationParams>): Translation | null {
        if (isNullOrUndefined(obj)) {
            return null
        }
        const resolvedDefaults = defaults ?? {}
        let _obj = getAnyValue(obj)
        let params: ITranslationParams
        switch (typeof _obj) {
            case "string":
                params = nsLanguage.parseString(_obj, resolvedDefaults)
                break
            case "object":
                const parsed = nsLanguage.parseString(_obj.k ?? _obj.string ?? '', resolvedDefaults)
                const translationParams: ITranslationParams = {
                    ac: _obj.ac ?? parsed.ac,
                    br: _obj.br ?? parsed.br,
                    c: _obj.c ?? parsed.c,
                    desc: _obj.desc ?? parsed.desc,
                    k: parsed.k,
                    n: _obj.n ?? parsed.n,
                    nc: _obj.nc ?? parsed.nc,
                    obj: _obj.obj ?? parsed.obj,
                    p: _obj.p ?? parsed.p,
                    r: _.defaultsDeep(_obj.r, parsed.r),
                    string: _obj.string ?? parsed.string,
                    suffix: _obj.suffix ?? parsed.suffix,
                    td: _obj.td ?? parsed.td,
                    uc: _obj.uc ?? parsed.uc,
                };
                params = _.defaultsDeep(translationParams, resolvedDefaults)
                break
            default:
                return this.$t(_obj, resolvedDefaults)
        }
        return new Translation(params, this.translations)
    }

    // translate
    public t(obj: TBaseOrObservableOrFunctionType<string | ITranslationParams>, defaults?: Partial<ITranslationParams>, onlyIfTranslationExists: boolean = false): string | null {
        const resolvedDefaults = defaults ?? {}
        if (onlyIfTranslationExists) {
            const o = getAnyValue(obj)
            let k: string
            if (typeof o === 'string') {
                k = o
            } else if (o && typeof o === 'object') {
                k = o.k ?? ''
            } else {
                k = ''
            }
            if (!this.hasTranslation(k) && !this.shouldBypassOnlyIfExists()) {
                return null
            }
        }
        const translation = this.$t(obj, resolvedDefaults)
        if (!translation) {
            return null
        }

        // CMS opt-in alias: displayname `@target.key` → resolve target with the same flags.
        const aliasKey = translation.getAliasKey()
        if (aliasKey) {
            const params = translation.getParams()
            const fromKey = params.k
            const fromLc = fromKey.toLowerCase()
            const toLc = aliasKey.toLowerCase()
            if (fromLc === toLc || this.aliasResolveStack.includes(toLc)) {
                return fromKey
            }
            this.aliasResolveStack.push(fromLc)
            try {
                return this.t({
                    ...params,
                    k: aliasKey,
                    string: aliasKey,
                }, resolvedDefaults)
            } finally {
                this.aliasResolveStack.pop()
            }
        }

        let ret: string | null = translation.toString()
        // Resolve nested ((key|flags)) segments in display text (see nsLanguage.parseString).
        if (ret && /\(\(.*\)\)/.test(ret)) {
            const input = getAnyValue(obj)
            const lookupKey = typeof input === 'string'
                ? nsLanguage.parseString(input, resolvedDefaults).k
                : input && typeof input === "object"
                    ? input.k ?? nsLanguage.parseString(input.string ?? input.k ?? '', resolvedDefaults).k
                    : null
            if (lookupKey) {
                // Some syncfields point back to themselves, which would recurse forever without this guard.
                // Example: lookup `wizard.targetorg` has display text `((wizard.targetorg))` or `((wizard.targetorg|ac))`.
                // Substitute the bare key so the UI shows `wizard.targetorg` instead of re-entering `t()`.
                ret = ret.replace(/\(\(((?:\"\(\(|\)\)\"|[^()])+)\)\)/g, (whole, inner) => {
                    const nestedKey = nsLanguage.parseString(inner, resolvedDefaults).k
                    return nestedKey?.toLowerCase() === lookupKey.toLowerCase() ? lookupKey : whole
                })
                // Circular case is fully resolved (e.g. `((wizard.targetorg))` → `wizard.targetorg`).
                if (!/\(\(.*\)\)/.test(ret)) {
                    return ret
                }
            }
            if (defaults === undefined) {
                const _obj = getAnyValue(obj)
                if (typeof _obj == "string") {
                    ret = this.t(ret)
                } else {
                    ret = this.t(ret, _obj)
                }
            } else {
                ret = this.t(ret, defaults)
            }
        }
        return ret
    }

    // factory for translate computed
    public tc(obj: TBaseOrObservableOrFunctionType<string | ITranslationParams>, defaults?: Partial<ITranslationParams>): KnockoutComputed<string | null> {
        return ko.pureComputed(() => this.t(obj, defaults))
    }

    // translate object type
    public to(objectTypeId: TBaseOrObservableType<ObjectTypeId>, defaults?: Partial<ITranslationParams>): string | null {
        const objDef = getObjectDefinition(getAnyValue(objectTypeId))
        if (!objDef) {
            return null
        }
        return this.$t(objDef.translation, defaults)?.toString() ?? null
    }

    // translate route label
    public tr(idOrDef: TRouteIdOrDefinition, defaults?: Partial<ITranslationParams>): string {
        return getRouteLabel(idOrDef, defaults)
    }

    public hasKey(key: string): boolean {
        return !!this.translations().find(o => o.name == key)
    }

    public hasTranslation(key: string): boolean {
        const syncfield = this.translations().find(o => o.name == key);
        return !!syncfield && !!syncfield.displayname
    }

    public getByKey(key: TSyncfield): ISyncField | undefined {
        if (key == null) {
            return undefined
        }
        const keyLc = String(key).toLowerCase()
        return this.translations().find(o => o.name.toLowerCase() == keyLc)
    }

    private shouldBypassOnlyIfExists(): boolean {
        return storeObservable('show.translation.key')() || storeObservable('prefix.translation')()
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.promiseRequest?.abort()
    }
}

export class Translation {
    private displaynames!: string[];
    private syncfield: ISyncField | undefined;
    private hasTranslation!: boolean;
    private hasConfig!: boolean;
    /** When CMS displayname is `@target.key`, hold the target for LanguageInstance.t to resolve. */
    private aliasKey: string | null = null;

    constructor(protected obj: ITranslationParams, protected translations: KnockoutObservableArray<ISyncField>) {
        this.init()
    }

    getAliasKey(): string | null {
        return this.aliasKey
    }

    getParams(): ITranslationParams {
        return this.obj
    }

    protected init(): void {
        const k = this.obj.k;
        this.syncfield = this.translations().find(o => o.name.toLowerCase() == k.toLowerCase())
        this.hasConfig = !!this.syncfield
        this.hasTranslation = !!this.syncfield?.displayname || !!this.syncfield?.description

        if (global.login?.isLoggedIn()) {
            if (!this.hasConfig) {
                if (!logConfig.has(k)) {
                    logConfig.add(k)
                }
            } else if (!this.hasTranslation) {
                if (!logTranslation.has(k)) {
                    logTranslation.add(k)
                }
            }
        }

        // Caller can opt into using the syncfield's description over its displayname
        // (currently only the password progress UI does this, to surface the longer
        // integration description configured per CompanySyncStep). Fall back to
        // displayname when no description is configured so the caller never gets
        // an empty string just because they asked for the description.
        const preferDescription = !!this.obj.desc && !!this.syncfield?.description;
        const rawText = preferDescription
            ? this.syncfield?.description
            : this.syncfield?.displayname == '#'
                ? this.syncfield?.description
                : this.syncfield?.displayname;

        this.aliasKey = parseTranslationAlias(rawText)
        if (this.aliasKey) {
            // Alias is resolved in LanguageInstance.t before toString / pipe-form selection.
            this.displaynames = [rawText ?? k]
            return
        }

        const a = rawText?.includes('|');
        const b = rawText?.includes('((') || rawText?.includes('{{');
        if (rawText && a && b) {
            const displayNames: string[] = []
            let lastIndex = 0
            let openParen = 0
            let openBrace = 0
            for (let i: number = 0; i < rawText.length; i++) {
                if (rawText[i] == Delimiter && openParen == 0 && openBrace == 0) {
                    displayNames.push(rawText.substring(lastIndex, i))
                    lastIndex = i + 1
                    continue
                }
                const sub = rawText.substring(i, i + 2);
                switch (sub) {
                    case '((':
                        openParen++;
                        i += 1;
                        break
                    case '))':
                        openParen--;
                        i += 1;
                        break
                    case '{{':
                        openBrace++;
                        i += 1;
                        break
                    case '}}':
                        openBrace--;
                        i += 1;
                        break
                }
            }
            displayNames.push(rawText.slice(lastIndex))
            this.displaynames = displayNames
        } else {
            this.displaynames = rawText?.split(Delimiter) ?? [k]
        }
    }

    toString(): string {
        let index: number
        if (this.obj.p || this.obj.n === 0) {
            index = this.displaynames.length - 1
        } else {
            index = Math.min(Math.max((this.obj.n ?? 1) - 1, 0), this.displaynames.length - 1);
            if (isNaN(index)) {
                index = 0
            }
        }
        let ret: string

        if (storeObservable('show.translation.key')()) {
            ret = this.obj.string ?? this.obj.k
            if (this.obj?.obj) {
                ret += `***`
            }
        } else {
            ret = this.displaynames[index] ?? '';
            ret = this.linebreaks(ret)
            ret = this.htmltags(ret)
            if (!this.obj.nc) {
                ret = this.lowercase(ret)
                ret = this.capitalize(ret)
                ret = this.uppercase(ret)
            }
            ret = this.trailingdots(ret)
            ret = this.suffix(ret)
            ret = this.replace(ret)
        }

        if (storeObservable('prefix.translation')() && this.syncfield && !nsSyncFields.hasTranslationIcon(ret) && !ret.startsWith('<')) {
            ret = nsSyncFields.getTranslationIcon(this.syncfield) + ret
        }

        return ret
    }

    protected linebreaks(str: string): string {
        if (this.obj.br) {
            return str.replace(/\n|\r/gm, () => {
                return '\n<br>'
            })
        }
        return str
    }

    protected htmltags(str: string): string {
        return str.replace(/(?:§(.*?)§)/g, (whole: string, str: string) => {
            return `<${str}>`
        })
    }

    protected capitalize(str: string): string {
        if (this.obj.c) {
            return str.charAt(0).toUpperCase() + str.substring(1)
        }
        return str
    }

    protected lowercase(str: string): string {
        if (this.obj.ac) {
            return str.charAt(0).toLowerCase() + str.substring(1)
        }
        return str
    }

    protected uppercase(str: string): string {
        if (this.obj.uc) {
            return str.toUpperCase()
        }
        return str
    }

    protected replace(str: string): string {
        // |n: also fills {{n}} in CMS text (raw number). Explicit r.n wins over n.
        const n = this.obj.n
        const replaceMap: Record<string, unknown> = {
            ...(typeof n === 'number' && !isNaN(n) ? {n: String(n)} : {}),
            ...(this.obj.r ?? {}),
        }
        const regExp_obj = /\{\{o\:(.*?)\}\}/g
        const shouldUseObjectProperty = regExp_obj.test(str);
        if (!Object.keys(replaceMap).length && !shouldUseObjectProperty) {
            return str
        }
        const regExp = /(\{\{(.*?)}})/gm;
        return str.replace(regExp, (str: string, placeholder: string, key: string) => {
            if (key.startsWith('o:')) {
                const path = key.substring(2)
                const replace = _.get(this.obj.obj, path)
                return replace ?? key
            }
            const replace = replaceMap[key];
            return replace ?? key
        })
    }

    protected trailingdots(str: string): string {
        if (this.obj.td) {
            return str + '...'
        }
        return str
    }

    protected suffix(str: string): string {
        if (this.obj.suffix) {
            return str + this.obj.suffix
        }
        return str
    }

    dispose() {
        // todo: is there a potential memory leak in this class that should be fixed?
    }

}
